import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { sendAnalyticsEvent } from '../utils/analyticsHeartbeat';
import { markActiveAnalyticsEngagement } from '../utils/backendRequestActivity';
import { logger } from '../utils/logger';

const SLIDE_PRESENTATION_EVENT_INTERVAL_MS = 30 * 1000;

type PresentationContextType = {
  isPresenting: boolean;
  setIsPresenting: (isPresenting: boolean) => void;
  isPreview: boolean;
  setIsPreview: (isPreview: boolean) => void;
};

const PresentationContext = createContext<PresentationContextType>({
  isPresenting: false,
  setIsPresenting: () => {},
  isPreview: false,
  setIsPreview: () => {},
});

export const PresentationProvider = ({ children }: { children: React.ReactNode }) => {
  const [isPresenting, setIsPresenting] = useState(false);
  const [isPreview, setIsPreview] = useState(false);

  const sendSlidePresentationEvent = useCallback(async () => {
    if (document.visibilityState === 'hidden') {
      return;
    }

    markActiveAnalyticsEngagement();

    try {
      await sendAnalyticsEvent({
        event_type: 'SlidePresentation',
        payload: {
          is_preview: isPreview
        }
      });
    } catch (error) {
      logger.debug('[AnalyticsV3] Failed to send SlidePresentation event', error);
    }
  }, [isPreview]);

  useEffect(() => {
    if (!isPresenting) {
      return undefined;
    }

    void sendSlidePresentationEvent();
    const intervalId = window.setInterval(() => {
      void sendSlidePresentationEvent();
    }, SLIDE_PRESENTATION_EVENT_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isPresenting, sendSlidePresentationEvent]);

  return (
    <PresentationContext.Provider value={{ isPresenting, setIsPresenting, isPreview, setIsPreview }}>
      {children}
    </PresentationContext.Provider>
  );
};

export const usePresentationContext = () => useContext(PresentationContext);
